import type { ButtonNode, MenuNode, MenuSchema, TextInputNode, TextNode } from '../MenuSchema.js';

export const DEVICE_MENU_ACTIONS = {
    connectToIntiface: 'connect-to-intiface-central',
    disconnectFromIntiface: 'disconnect-from-intiface-central',
    enterEnvironment: 'device-enter-environment',
    leaveEnvironment: 'device-leave-environment',
    toggleHapticsHud: 'device-toggle-haptics-hud',
    selectProstheticNone: 'device-select-prosthetic-none',
    selectProstheticInny: 'device-select-prosthetic-inny',
    selectProstheticOuty: 'device-select-prosthetic-outy',
    sendDeviceCommand: 'send-intiface-device-command',
    stopDeviceCommand: 'stop-intiface-device-command',
    sendRemoteDeviceCommand: 'send-remote-device-command',
    stopRemoteDeviceCommand: 'stop-remote-device-command',
} as const;

export const DEVICE_MENU_LABELS = {
    title: 'Haptic Devices',
    intifaceUrl: 'Intiface URL:',
    connect: 'Connect',
    connecting: 'Connecting',
    disconnect: 'Disconnect',
    environment: 'Environment',
    leaveEnvironment: 'Leave Environment',
    showHapticsHud: 'Show Haptics HUD',
    hideHapticsHud: 'Hide Haptics HUD',
    prosthetic: 'Prosthetic',
    prostheticNone: 'None',
    prostheticInny: 'Inny',
    prostheticOuty: 'Outy',
    permitRemoteControl: 'Permit Remote Control',
    revokeRemoteControl: 'Revoke Remote Control',
    sendVibration: 'Send Vibration',
    stopAll: 'Stop All',
    send: 'Send',
    stop: 'Stop',
    noDevicesDetected: 'No devices detected.',
    connectToSeeDevices: 'Connect to Intiface to see devices.',
    intifaceUrlPlaceholder: 'ws://127.0.0.1:12345',
} as const;

export function createDeviceMenuSchema(): MenuSchema {
    return {
        id: 'DEVICE',
        title: DEVICE_MENU_LABELS.title,
        nodes: [
            { type: 'text', text: DEVICE_MENU_LABELS.intifaceUrl, platforms: ['web'] },
            { type: 'input', id: 'device-intiface-url-input', platforms: ['web'], placeholder: DEVICE_MENU_LABELS.intifaceUrlPlaceholder, get: () => '' },
            { type: 'button', id: DEVICE_MENU_ACTIONS.connectToIntiface, platforms: ['web'], label: DEVICE_MENU_LABELS.connect },
            { type: 'button', id: DEVICE_MENU_ACTIONS.enterEnvironment, platforms: ['web'], label: DEVICE_MENU_LABELS.environment },
            { type: 'button', id: DEVICE_MENU_ACTIONS.leaveEnvironment, platforms: ['web'], label: DEVICE_MENU_LABELS.leaveEnvironment },
            { type: 'button', id: DEVICE_MENU_ACTIONS.toggleHapticsHud, label: DEVICE_MENU_LABELS.showHapticsHud },
            { type: 'text', text: DEVICE_MENU_LABELS.prosthetic },
            { type: 'button', id: DEVICE_MENU_ACTIONS.selectProstheticNone, label: DEVICE_MENU_LABELS.prostheticNone },
            { type: 'button', id: DEVICE_MENU_ACTIONS.selectProstheticInny, label: DEVICE_MENU_LABELS.prostheticInny },
            { type: 'button', id: DEVICE_MENU_ACTIONS.selectProstheticOuty, label: DEVICE_MENU_LABELS.prostheticOuty },
            { type: 'button', id: 'device-permit-remote-control', platforms: ['web'], label: DEVICE_MENU_LABELS.permitRemoteControl },
            { type: 'button', id: 'device-revoke-remote-control', platforms: ['web'], label: DEVICE_MENU_LABELS.revokeRemoteControl },
            { type: 'button', id: DEVICE_MENU_ACTIONS.sendDeviceCommand, platforms: ['web'], label: DEVICE_MENU_LABELS.sendVibration },
            { type: 'button', id: DEVICE_MENU_ACTIONS.stopDeviceCommand, platforms: ['web'], label: DEVICE_MENU_LABELS.stopAll },
            { type: 'button', id: 'device-footer-send', platforms: ['web'], label: DEVICE_MENU_LABELS.send },
            { type: 'button', id: 'device-footer-stop', platforms: ['web'], label: DEVICE_MENU_LABELS.stop },
        ],
    };
}

function findSchemaNodeById(schema: MenuSchema, id: string): MenuNode | undefined {
    return schema.nodes.find((n) => (n as { id?: string }).id === id);
}

export function readDeviceSchemaButtonLabel(schema: MenuSchema, id: string, fallback: string): string {
    const node = findSchemaNodeById(schema, id) as ButtonNode | undefined;
    if (!node || node.type !== 'button') {
        return fallback;
    }
    return typeof node.label === 'function' ? node.label() : node.label;
}

export function readDeviceSchemaInputPlaceholder(schema: MenuSchema, id: string, fallback: string): string {
    const node = findSchemaNodeById(schema, id) as TextInputNode | undefined;
    if (!node || node.type !== 'input') {
        return fallback;
    }
    return node.placeholder || fallback;
}

export function readDeviceSchemaTextAt(schema: MenuSchema, index: number, fallback: string): string {
    const textNodes = schema.nodes.filter((n): n is TextNode => n.type === 'text');
    const node = textNodes[index];
    if (!node) {
        return fallback;
    }
    return typeof node.text === 'function' ? node.text() : node.text;
}
